import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ConnectError, Code } from '@connectrpc/connect';
import type {
  AuditBatch,
  AuditCase,
  AuditSubmission,
  CodebookHit,
  CodeCaseList,
  CodeCaseQuery,
  MyDashboard,
  OrgDashboard,
  AuditQueue,
} from './types';

// Parallel data seam for the audit workbench (HUB-504). Deliberately SEPARATE from
// the generic BladeDataSource: audit views need per-field A/B code arrays, batch
// aggregates, and a codebook — shapes the thin fetchList/fetchEntity contract can't
// carry. Audit primitives call the hooks below, which resolve against whatever
// AuditDataSource the shell provides (a static mock today; a live @hub/api source
// when the backend lands).
//
// This is the seam through which (eventually real) PHI would enter the audit UI,
// so it inherits the shell's rules: never log payloads, keep entity ids — never
// payloads — in query keys, and surface permission failures as a "denied" state.
//
// BLIND-REVIEW INVARIANT: `fetchCase` returns the BLIND `AuditCase` (opaque A/B
// slots, no provenance). The source performs the agent/coder→A/B blinding before
// anything reaches a view; nothing here exposes which slot is the agent.

export interface AuditDataSource {
  /** all batches for the auditor's queue (one day × facility × client each) */
  fetchBatches(): Promise<AuditBatch[]>;
  /** the reviewer queues and their assignments, for showing WHO has what */
  fetchQueues(): Promise<AuditQueue[]>;
  /** a single batch by id (its case summaries), or null if unknown */
  fetchBatch(id: string): Promise<AuditBatch | null>;
  /** a single BLIND case by id (opaque A/B results), or null if unknown */
  fetchCase(id: string): Promise<AuditCase | null>;
  /** codebook lookup for the reviewer's code picker */
  searchCodebook(query: string): Promise<CodebookHit[]>;
  /** submit a completed review (no-op in the mock) */
  submitAudit(submission: AuditSubmission): Promise<void>;
  /** aggregate org dashboard (provenance used only in aggregate) */
  fetchOrgDashboard(): Promise<OrgDashboard>;
  /** auditor-only; the cases behind one report row */
  fetchCodeCases(q: CodeCaseQuery): Promise<CodeCaseList>;
  /** the auditor's personal weekly stats */
  fetchMyDashboard(): Promise<MyDashboard>;
}

interface AuditDataContextValue {
  source: AuditDataSource;
  /**
   * Namespaces this provider's query cache.
   *
   * Load-bearing when providers are NESTED. The walkthrough re-provides a demo
   * source for the views below it, and without a distinct scope every hook
   * would key on ['audit', 'batches'] and be served the OUTER source's cached
   * answer — the substitution would appear to do nothing, which is exactly how
   * it first behaved.
   */
  scope: string;
}

const AuditDataContext = createContext<AuditDataContextValue | null>(null);

export function AuditDataProvider({
  source,
  scope = 'live',
  children,
}: {
  source: AuditDataSource;
  /** Cache namespace. Give a nested provider its own. */
  scope?: string;
  children: ReactNode;
}) {
  const value = useMemo(() => ({ source, scope }), [source, scope]);
  return <AuditDataContext.Provider value={value}>{children}</AuditDataContext.Provider>;
}

function useAuditContext(): AuditDataContextValue {
  const s = useContext(AuditDataContext);
  if (!s) throw new Error('useAuditBatches/useAuditBatch/useAuditCase require an AuditDataProvider');
  return s;
}

function useAuditSource(): AuditDataSource {
  return useAuditContext().source;
}

/** The cache namespace for this provider's queries. */
function useAuditScope(): string {
  return useAuditContext().scope;
}

/**
 * The batch queue, or null when no provider is mounted.
 *
 * Same reasoning as useAuditTour returning null: WorkbenchShell is mounted in
 * tests and in the backendless preview without an audit source, and help
 * content must degrade to absent rather than crash the shell. The walkthrough's
 * demo-data gate consults this, and it briefly DID crash those shells by using
 * the throwing hook.
 */
export function useOptionalAuditBatches(): AuditQuery<AuditBatch[]> | null {
  const ctx = useContext(AuditDataContext);
  return useBatchesFrom(ctx);
}

/** Shared by useAuditBatches and its optional twin so the query config cannot
 *  drift between them. */
function useBatchesFrom(ctx: AuditDataContextValue | null): AuditQuery<AuditBatch[]> | null {
  const q = useQuery({
    queryKey: ['audit', ctx?.scope ?? 'none', 'batches'],
    queryFn: () => ctx!.source.fetchBatches(),
    enabled: ctx !== null,
    staleTime: 10 * 1000,
  });
  if (!ctx) return null;
  if (q.isPending) return { status: 'loading' };
  if (q.isError) return { status: statusForError(q.error) };
  return { status: 'success', data: q.data };
}

/**
 * The reviewer queues.
 *
 * Separate from batches rather than folded into them: a batch's assignment is
 * a property of the QUEUES, not of the batch, and ListBatches does not carry
 * it. Keeping them separate also means a queue-read failure degrades the
 * assignment column to absent instead of emptying the batch list.
 */
export function useAuditQueues(): AuditQuery<AuditQueue[]> | null {
  const ctx = useContext(AuditDataContext);
  const q = useQuery({
    queryKey: ['audit', ctx?.scope ?? 'none', 'queues'],
    queryFn: () => ctx!.source.fetchQueues(),
    enabled: ctx !== null,
    staleTime: 10 * 1000,
  });
  if (!ctx) return null;
  if (q.isPending) return { status: 'loading' };
  if (q.isError) return { status: statusForError(q.error) };
  return { status: 'success', data: q.data };
}

/**
 * The source, for the codebook hooks.
 *
 * Exported because code lookup lives in its own module (data/codebookSource.ts)
 * rather than here: it is reference data with its own caching rules — cached
 * forever, never retried on a miss — and folding it into the PHI-bearing case
 * hooks would put those rules next to data that must never be cached that way.
 */
export function useAuditCodebookSource(): AuditDataSource {
  return useAuditSource();
}

export type AuditQueryStatus = 'loading' | 'error' | 'denied' | 'success';

export interface AuditQuery<T> {
  status: AuditQueryStatus;
  data?: T;
}

/** Map a thrown error to a status — permission failures become "denied". */
function statusForError(error: unknown): 'denied' | 'error' {
  if (error instanceof ConnectError) {
    if (error.code === Code.PermissionDenied || error.code === Code.Unauthenticated) {
      return 'denied';
    }
  }
  return 'error';
}

/** The auditor's batch queue. PHI-free query key (no case/patient identifiers). */
export function useAuditBatches(): AuditQuery<AuditBatch[]> {
  const ctx = useAuditContext();
  return useBatchesFrom(ctx)!;
}

/** A single batch (its case summaries). Key carries the batch id only. */
export function useAuditBatch(id: string | undefined): AuditQuery<AuditBatch> {
  const source = useAuditSource();
  const scope = useAuditScope();
  const enabled = !!id;
  const q = useQuery({
    queryKey: ['audit', scope, 'batch', id ?? null],
    queryFn: () => source.fetchBatch(id!),
    enabled,
    staleTime: 10 * 1000,
  });
  if (!enabled) return { status: 'success' };
  if (q.isPending) return { status: 'loading' };
  if (q.isError) return { status: statusForError(q.error) };
  return { status: 'success', data: q.data ?? undefined };
}

/**
 * The review-submission action.
 *
 * The returned promise is the ONLY signal that the review was recorded, so
 * callers must await it and treat a rejection as "not submitted": the compare
 * screen keeps the case open and unmarked. It used to be called as
 * `void submitAudit(...)`, which made a PermissionDenied or a dropped
 * connection look exactly like success — the reviewer advanced, the batch
 * counted the case done, and the work existed only in localStorage.
 *
 * The payload carries only field verdict ids + reason ids + notes, never slot
 * provenance, keeping the blind invariant intact end to end.
 */
export function useAuditSubmit(): (submission: AuditSubmission) => Promise<void> {
  const source = useAuditSource();
  return useCallback(
    async (submission: AuditSubmission) => {
      await source.submitAudit(submission);
    },
    [source],
  );
}

/** Study-level program metrics: agent vs coder as judged by reviewers.
 *
 *  The one surface that speaks in provenance, and safe because it is an
 *  aggregate — no key, no id, nothing that ties a rate back to a case a
 *  reviewer currently has open. Refetched on a shorter stale time than the
 *  queue: a dashboard is read to decide something, and a stale accuracy number
 *  is worse than a spinner.
 */
export function useOrgDashboard(): AuditQuery<OrgDashboard> {
  const source = useAuditSource();
  const scope = useAuditScope();
  const q = useQuery({
    queryKey: ['audit', scope, 'org-dashboard'],
    queryFn: () => source.fetchOrgDashboard(),
    staleTime: 30 * 1000,
  });
  if (q.isPending) return { status: 'loading' };
  if (q.isError) return { status: statusForError(q.error) };
  return { status: 'success', data: q.data };
}

/** A single BLIND case. Key carries the case id only — never A/B payloads. */
export function useAuditCase(id: string | undefined): AuditQuery<AuditCase> {
  const source = useAuditSource();
  const scope = useAuditScope();
  const enabled = !!id;
  const q = useQuery({
    queryKey: ['audit', scope, 'case', id ?? null],
    queryFn: () => source.fetchCase(id!),
    enabled,
    staleTime: 10 * 1000,
  });
  if (!enabled) return { status: 'success' };
  if (q.isPending) return { status: 'loading' };
  if (q.isError) return { status: statusForError(q.error) };
  return { status: 'success', data: q.data ?? undefined };
}

/** The cases behind a report row. Key carries the QUERY only — a mode, a
 *  field, a code, a reason id — never a payload. */
export function useCodeCases(q: CodeCaseQuery | undefined): AuditQuery<CodeCaseList> {
  const source = useAuditSource();
  const scope = useAuditScope();
  const enabled = !!q;
  const query = useQuery({
    queryKey: ['audit', scope, 'code-cases', q?.mode ?? '', q?.field ?? '', q?.code ?? '', q?.reason ?? ''],
    queryFn: () => source.fetchCodeCases(q!),
    enabled,
    staleTime: 30 * 1000,
  });
  if (!enabled) return { status: 'success' };
  if (query.isPending) return { status: 'loading' };
  if (query.isError) return { status: statusForError(query.error) };
  return { status: 'success', data: query.data };
}
